import { AssignmentSections, PageTypes, PageViews, RouteResources, RubricSections, ScopeTypes } from './constants';

const getRoutePathname = (pathname = '') => {
    let rawPathname = pathname.split(/[?#]/)[0];

    if (pathname.startsWith('http')) {
        try {
            rawPathname = new URL(pathname).pathname;
        } catch {
            rawPathname = pathname.split(/[?#]/)[0];
        }
    }

    const pathnameWithoutLocale = rawPathname.replace(/^\/[a-z]{2}(?=\/|$)/, '') || '/';
    return pathnameWithoutLocale.replace(/\/+$/, '') || '/';
};

const buildRouteContext = ({
    assignmentId = null,
    detailId = null,
    pageType,
    pageView = null,
    scopeType = ScopeTypes.USER,
}) => ({
    assignmentId,
    detailId,
    pageType,
    pageView,
    scopeType,
});

const createAssignmentRouteContext = ({ assignmentId = null, detailId = null, pageType, pageView = null }) =>
    buildRouteContext({
        assignmentId,
        detailId,
        pageType,
        pageView,
        scopeType: assignmentId ? ScopeTypes.ASSIGNMENT : ScopeTypes.COURSE,
    });

const RouteMatchers = [
    {
        pattern: /^\/$/,
        toRouteContext: () =>
            buildRouteContext({
                pageType: PageTypes.COURSE_LIST,
            }),
    },
    {
        pattern: new RegExp(`^/${RouteResources.ASSIGNMENTS}$`),
        toRouteContext: () =>
            createAssignmentRouteContext({
                pageType: PageTypes.ASSIGNMENT_LIST,
            }),
    },
    {
        pattern: new RegExp(`^/${RouteResources.ASSIGNMENTS}/${AssignmentSections.NEW}$`),
        toRouteContext: () =>
            createAssignmentRouteContext({
                pageType: PageTypes.ASSIGNMENT_CREATE,
            }),
    },
    {
        pattern: new RegExp(`^/${RouteResources.ASSIGNMENTS}/([^/]+)$`),
        toRouteContext: ([, assignmentId]) =>
            createAssignmentRouteContext({
                assignmentId,
                pageType: PageTypes.ASSIGNMENT_OVERVIEW,
            }),
    },
    {
        pattern: new RegExp(`^/${RouteResources.ASSIGNMENTS}/([^/]+)/${AssignmentSections.EDIT}$`),
        toRouteContext: ([, assignmentId]) =>
            createAssignmentRouteContext({
                assignmentId,
                pageType: PageTypes.ASSIGNMENT_EDIT,
            }),
    },
    {
        pattern: new RegExp(`^/${RouteResources.ASSIGNMENTS}/([^/]+)/${AssignmentSections.PREVIEW}$`),
        toRouteContext: ([, assignmentId]) =>
            createAssignmentRouteContext({
                assignmentId,
                pageType: PageTypes.ASSIGNMENT_PREVIEW,
            }),
    },
    {
        pattern: new RegExp(`^/${RouteResources.ASSIGNMENTS}/([^/]+)/${AssignmentSections.GRADING}(?:/([^/]+))?$`),
        toRouteContext: ([, assignmentId, submissionId]) =>
            createAssignmentRouteContext({
                assignmentId,
                detailId: submissionId || null,
                pageType: PageTypes.GRADING,
            }),
    },
    {
        pattern: new RegExp(`^/${RouteResources.ASSIGNMENTS}/([^/]+)/${AssignmentSections.SUBMISSIONS}$`),
        toRouteContext: ([, assignmentId]) =>
            createAssignmentRouteContext({
                assignmentId,
                pageType: PageTypes.SUBMISSION_LIST,
                pageView: PageViews.SUBMISSIONS,
            }),
    },
    {
        pattern: new RegExp(`^/${RouteResources.ASSIGNMENTS}/([^/]+)/${AssignmentSections.SUBMISSIONS}/([^/]+)$`),
        toRouteContext: ([, assignmentId, submissionId]) =>
            createAssignmentRouteContext({
                assignmentId,
                detailId: submissionId,
                pageType: PageTypes.SUBMISSION_DETAIL,
            }),
    },
    {
        pattern: new RegExp(`^/${RouteResources.PEOPLE}$`),
        toRouteContext: () =>
            buildRouteContext({
                pageType: PageTypes.PEOPLE_LIST,
                scopeType: ScopeTypes.COURSE,
            }),
    },
    {
        pattern: new RegExp(`^/${RouteResources.RUBRICS}$`),
        toRouteContext: () =>
            buildRouteContext({
                pageType: PageTypes.RUBRIC_LIST,
                scopeType: ScopeTypes.COURSE,
            }),
    },
    {
        pattern: new RegExp(`^/${RouteResources.RUBRICS}/(?:${RubricSections.GET_STARTED}|${RubricSections.NEW})$`),
        toRouteContext: () =>
            buildRouteContext({
                pageType: PageTypes.RUBRIC_CREATE,
                scopeType: ScopeTypes.COURSE,
            }),
    },
    {
        pattern: new RegExp(`^/${RouteResources.RUBRICS}/([^/]+)$`),
        toRouteContext: ([, rubricId]) =>
            buildRouteContext({
                detailId: rubricId,
                pageType: PageTypes.RUBRIC_OVERVIEW,
                scopeType: ScopeTypes.COURSE,
            }),
    },
    {
        pattern: new RegExp(`^/${RouteResources.RUBRICS}/([^/]+)/${RubricSections.EDIT}$`),
        toRouteContext: ([, rubricId]) =>
            buildRouteContext({
                detailId: rubricId,
                pageType: PageTypes.RUBRIC_EDIT,
                scopeType: ScopeTypes.COURSE,
            }),
    },
];

const getUnknownRouteContext = () =>
    buildRouteContext({
        pageType: PageTypes.UNKNOWN,
    });

const getRouteContext = (pathname = '') => {
    const routePathname = getRoutePathname(pathname);

    for (const { pattern, toRouteContext } of RouteMatchers) {
        const match = routePathname.match(pattern);
        if (match) return toRouteContext(match);
    }

    return getUnknownRouteContext();
};

export const buildSessionContext = ({ auth, pathname }) => {
    const routeContext = getRouteContext(pathname);
    const courseId = routeContext.scopeType === ScopeTypes.USER ? null : auth?.course_id || null;

    return {
        pageContext: {
            page_type: routeContext.pageType,
            ...(routeContext.pageView ? { active_tab: routeContext.pageView } : {}),
            ...(routeContext.detailId ? { selected_item_id: routeContext.detailId } : {}),
        },
        routeContext,
        scope: {
            type: courseId ? routeContext.scopeType : ScopeTypes.USER,
            course_id: courseId,
            assignment_id:
                courseId && routeContext.scopeType === ScopeTypes.ASSIGNMENT ? routeContext.assignmentId : null,
        },
    };
};
